import { TransitionFeeItem, TransitionPromotionInput, TransitionResponse, TransitionStage, TransitionStudent } from "../types";

// Mocks the backend described in the Year Transition API design:
// GET  /academic-years/transition/status
// POST /academic-years/transition/promote
// POST /academic-years/transition/continue
// POST /academic-years/transition/fee-structure
// Swap these for real axiosInstance calls once the backend exists — the
// envelope shape (TransitionResponse) is the real contract, this file just
// simulates the server holding progression state between calls.

const MOCK_DELAY = 500;

const MOCK_SECTIONS: { grade: string; section: string; students: TransitionStudent[] }[] = [
  {
    grade: "Ten",
    section: "A",
    students: [
      { admissionNo: "1001", name: "Aarav Shah", guardianPhone: "9876500001", totalDue: 0 },
      { admissionNo: "1002", name: "Diya Patel", guardianPhone: "9876500002", totalDue: 1500 },
    ],
  },
  {
    grade: "Ten",
    section: "B",
    students: [{ admissionNo: "1010", name: "Kabir Mehta", guardianPhone: "9876500010", totalDue: 0 }],
  },
  {
    grade: "Nine",
    section: "A",
    students: [{ admissionNo: "1020", name: "Ishaan Rao", guardianPhone: "9876500020", totalDue: 0 }],
  },
  {
    grade: "PRE-KG",
    section: "A",
    students: [{ admissionNo: "1030", name: "Myra Nair", guardianPhone: "9876500030", totalDue: 0 }],
  },
];

const MOCK_FEE_STRUCTURE: TransitionFeeItem[] = [
  { name: "PRE-KG", currentFee: 12000 },
  { name: "Nine", currentFee: 18000 },
  { name: "Ten", currentFee: 20000 },
];

const NEW_ACADEMIC_YEAR_NAME = "AY-26-27";
const OLD_ACADEMIC_YEAR_NAME = "AY-25-26";

let stage: TransitionStage = TransitionStage.PROMOTE;
let sectionIndex = 0;

const delay = <T,>(value: T): Promise<T> => new Promise((resolve) => setTimeout(() => resolve(value), MOCK_DELAY));

const currentResponse = (): TransitionResponse => {
  switch (stage) {
    case TransitionStage.PROMOTE: {
      const section = MOCK_SECTIONS[sectionIndex];
      return {
        stage: TransitionStage.PROMOTE,
        success: true,
        grade: section.grade,
        section: section.section,
        data: { students: section.students },
        message: `Promote students in ${section.grade} - ${section.section}`,
      };
    }
    case TransitionStage.YEAR_CREATED:
      return {
        stage: TransitionStage.YEAR_CREATED,
        success: true,
        data: { academicYear: { name: NEW_ACADEMIC_YEAR_NAME, status: "PLANNED" } },
        message: `Created academic year ${NEW_ACADEMIC_YEAR_NAME}.`,
      };
    case TransitionStage.YEAR_CLOSED:
      return {
        stage: TransitionStage.YEAR_CLOSED,
        success: true,
        data: { academicYear: { name: OLD_ACADEMIC_YEAR_NAME, status: "CLOSED" } },
        message: `Closed academic year ${OLD_ACADEMIC_YEAR_NAME}.`,
      };
    case TransitionStage.FEE_STRUCTURE:
      return {
        stage: TransitionStage.FEE_STRUCTURE,
        success: true,
        data: { feeStructure: MOCK_FEE_STRUCTURE },
        message: `Define fees for ${NEW_ACADEMIC_YEAR_NAME}.`,
      };
    case TransitionStage.INITIALIZED:
      return {
        stage: TransitionStage.INITIALIZED,
        success: true,
        data: { academicYear: { name: NEW_ACADEMIC_YEAR_NAME, status: "INITIALIZED" } },
        message: `Initialized ${NEW_ACADEMIC_YEAR_NAME}.`,
      };
    case TransitionStage.COMPLETE:
      return {
        stage: TransitionStage.COMPLETE,
        success: true,
        data: { academicYear: { name: NEW_ACADEMIC_YEAR_NAME, status: "ACTIVE" } },
        message: `${NEW_ACADEMIC_YEAR_NAME} is now active. Transition complete.`,
      };
  }
};

export const getTransitionStatus = (): Promise<TransitionResponse> => delay(currentResponse());

export const submitPromotion = (_grade: string, _section: string, _promotions: TransitionPromotionInput[]): Promise<TransitionResponse> => {
  sectionIndex += 1;
  if (sectionIndex >= MOCK_SECTIONS.length) {
    stage = TransitionStage.YEAR_CREATED;
  }
  return delay(currentResponse());
};

export const continueTransition = (): Promise<TransitionResponse> => {
  if (stage === TransitionStage.YEAR_CREATED) stage = TransitionStage.YEAR_CLOSED;
  else if (stage === TransitionStage.YEAR_CLOSED) stage = TransitionStage.FEE_STRUCTURE;
  else if (stage === TransitionStage.INITIALIZED) stage = TransitionStage.COMPLETE;
  return delay(currentResponse());
};

export const submitFeeStructure = (_feeStructure: { name: string; newFee: number }[]): Promise<TransitionResponse> => {
  stage = TransitionStage.INITIALIZED;
  return delay(currentResponse());
};
